import {TagType} from "./enums";
import {EntityType} from "../routines/schemas";
import {ExchangeTypeTagNames} from "../routines/schemas/transcript_analysis";

// Core data for Tags, shared between client and DB representations
// Note: Tags with type=FOLDER_UPLOAD replace the legacy Folder model
export interface TagRead {
    id: string;
    tag_name: string;
    type: TagType;  // No default value in interfaces
    entity_type?: EntityType;  // Only set when type = TagType.ENTITY
    description?: string;
    system_context?: string;

    group_id: string;
    submitter_id: string;

    chat_id?: string;  // Optional, used for chat-specific tags
    document_request_id?: string;
    ai_generated?: boolean;  // Whether this tag was AI-generated (e.g., from deep research/investigation)

    // Folder-like properties (primarily used for TagType.FOLDER_UPLOAD)
    created_at: string;  // Creation timestamp
    document_count: number;  // Number of documents associated with this tag
}

// For creation, created_at and document_count are server-provided
export type TagCreate = Omit<TagRead, "id" | "created_at" | "document_count"> & {
    created_at?: string;
    document_count?: number;
};

export interface TagUpdate {
    id: string;
    tag_name?: string;
    type?: TagType;
    description?: string;
    system_context?: string;
    chat_id?: string;  // Optional, used for chat-specific tags
    document_request_id?: string;
    ai_generated?: boolean;
    document_count?: number;
}

// Search results containing multiple tags
export type TagResults = {
    tags: TagRead[];
}

/**
 * Tag types that participate in the scoring pipeline (pre-created associations).
 * See also: backend TagType.association_consistent_types() and
 * requires_association_consistency() in src/schemas/tag.py
 */
const ASSOCIATION_CONSISTENT_TYPES: TagType[] = [TagType.TAG, TagType.REQUEST_SET];

const EXCHANGE_TYPE_TAG_NAMES_SET = new Set(
    Object.values(ExchangeTypeTagNames).map(n => n.toLowerCase())
);

/**
 * Whether a tag participates in the scoring pipeline (has pre-created associations).
 * Excludes entity/privilege/folder tags AND exchange type tags (transcript categorization).
 * Mirrors backend requires_association_consistency() in src/schemas/tag.py.
 */
export function requiresAssociationConsistency(tag: TagRead): boolean {
    if (!ASSOCIATION_CONSISTENT_TYPES.includes(tag.type)) return false;
    if (EXCHANGE_TYPE_TAG_NAMES_SET.has(tag.tag_name.toLowerCase())) return false;
    return true;
}

/** Whether a tag is a transcript exchange type tag (Admissions, Denials, etc.) */
export function isTranscriptExchangeTag(tag: TagRead): boolean {
    return tag.type === TagType.TAG && EXCHANGE_TYPE_TAG_NAMES_SET.has(tag.tag_name.toLowerCase());
}

// ─── Witness credibility ────────────────────────────────────────────────────
// Mirrors backend src/services/credibility_service.py — aggregated profile of
// a person tag's transcript exchanges (admissions, denials, inconsistencies, ...)

import {ExchangeType} from "./enums";

export interface NotableExchange {
    reference_id: string;
    doc_id: string;
    doc_title?: string;
    page_start?: number;
    exchange_type: ExchangeType;
    quote: string;
    explanation?: string;
}

export interface PersonCredibility {
    person_tag_id: string;
    total_exchanges: number;
    counts_by_type: Partial<Record<ExchangeType, number>>;
    notable_exchanges: NotableExchange[];
}
